/**
 * Store - Redux-Enhanced State Management
 * 
 * This module provides the main store interface that combines
 * Redux patterns with the existing AppState functionality.
 * It serves as the central export point for all store-related functionality.
 * 
 * @module store/index
 */

// Import core store components
import AppState from '../core/AppState.js';
import Config from '../core/Config.js';
import { actions, ActionTypes, asyncActions, isValidAction, batchActions } from './actions.js';
import { 
  rootReducer, 
  uiReducer, 
  initiativesReducer, 
  chatReducer, 
  userReducer, 
  strategiesReducer, 
  portfolioReducer, 
  systemReducer,
  selectionReducer,
  getReducerForDomain,
  shouldReducerHandle
} from './reducers.js';
import {
  createDefaultMiddleware,
  loggerMiddleware,
  persistenceMiddleware,
  analyticsMiddleware,
  asyncMiddleware,
  validationMiddleware,
  performanceMiddleware,
  eventBridgeMiddleware,
  errorMiddleware,
  debounceMiddleware,
  devToolsMiddleware,
  composeMiddleware,
  applyMiddleware
} from './middleware.js';
import * as selectors from './selectors.js';

/**
 * Enhanced Store Class
 * Provides a Redux-like interface while maintaining backward compatibility
 */
class Store {
  constructor() {
    console.log('🏪 Initializing Enhanced Store...');
    
    // Use the enhanced AppState as the core
    this.appState = AppState;
    this.isReady = false;
    
    // Bind methods for consistent API
    this.getState = this.appState.getState.bind(this.appState);
    this.dispatch = this.appState.dispatch.bind(this.appState);
    this.subscribe = this.appState.subscribe.bind(this.appState);
    
    // Store metadata
    this.meta = {
      version: Config.APP_VERSION,
      reduxEnhanced: true,
      createdAt: new Date().toISOString(),
      features: [
        'redux-patterns',
        'middleware-pipeline',
        'devtools-integration',
        'action-history',
        'memoized-selectors',
        'backward-compatibility'
      ]
    };
    
    this.isReady = true;
    console.log('✅ Enhanced Store initialized successfully');
  }
  
  /**
   * Get current state with optional selector
   * @param {Function} [selector] - Optional selector function
   * @param {...any} args - Arguments for the selector
   * @returns {*} State or selected value
   */
  select(selector, ...args) {
    if (!selector) return this.getState();
    return selector(this.getState(), ...args);
  }
  
  /**
   * Dispatch multiple actions as a batch
   * @param {Array} actionList - Array of actions to dispatch
   * @returns {Array} Results of each action
   */
  batchDispatch(actionList) {
    if (!Array.isArray(actionList)) {
      throw new Error('batchDispatch requires an array of actions');
    }
    
    return this.dispatch(batchActions(actionList));
  }
  
  /**
   * Create a scoped dispatch function for a specific domain
   * @param {string} domain - Domain name (ui, initiatives, chat, etc.)
   * @returns {Function} Scoped dispatch function
   */
  createScopedDispatch(domain) {
    return (action) => {
      // Add domain metadata
      const scopedAction = {
        ...action,
        meta: {
          ...action.meta,
          domain,
          scoped: true
        }
      };
      
      return this.dispatch(scopedAction);
    };
  }
  
  /**
   * Get store statistics and health info
   * @returns {Object} Store statistics
   */
  getStats() {
    const baseStats = this.appState.getStats();
    
    return {
      ...baseStats,
      meta: this.meta,
      state: {
        initiatives: this.select(selectors.getInitiativeCount),
        chatMessages: this.select(selectors.getChatMessageCount),
        errors: this.select(selectors.getErrorCount),
        hasApiKey: !!this.select(selectors.getApiKey),
        hasStrategy: this.select(selectors.getHasStrategy)
      },
      health: this.select(selectors.getApplicationHealth)
    };
  }
  
  /**
   * Reset store to initial state
   * @param {Object} [options] - Reset options
   */
  reset(options = {}) {
    console.log('🔄 Resetting store...', options);
    return this.appState.reset(options.clearStorage);
  }
  
  /**
   * Export complete store state and history
   * @returns {Object} Exportable store state
   */
  export() {
    return {
      ...this.appState.exportState(),
      meta: this.meta,
      stats: this.getStats()
    };
  }
  
  /**
   * Import store state from export
   * @param {Object} exportedData - Previously exported store data
   */
  import(exportedData) {
    console.log('📥 Importing store state...');
    this.appState.importState(exportedData);
  }
  
  /**
   * Create a snapshot of current state for debugging
   * @param {string} [label] - Optional label for the snapshot
   * @returns {Object} State snapshot
   */
  snapshot(label = 'manual') {
    return {
      label,
      timestamp: new Date().toISOString(),
      state: JSON.parse(JSON.stringify(this.getState())),
      stats: this.getStats()
    };
  }
  
  /**
   * Time travel debugging (if DevTools available)
   * @param {number} actionIndex - Index in action history to jump to
   */
  jumpToAction(actionIndex) {
    if (this.appState.devTools) {
      this.appState.devTools.send({
        type: '@@JUMP_TO_ACTION',
        payload: { actionIndex }
      }, this.getState());
    } else {
      console.warn('Time travel requires Redux DevTools to be connected');
    }
  }
  
  /**
   * Get action history
   * @returns {Array} Action history
   */
  getHistory() {
    return this.appState.getHistory();
  }
  
  /**
   * Clear action history
   */
  clearHistory() {
    this.appState.actionHistory = [];
    console.log('🗑️ Action history cleared');
  }
  
  /**
   * Replace the current reducer
   * @param {Function} nextReducer - New root reducer
   */
  replaceReducer(nextReducer) {
    if (typeof nextReducer !== 'function') {
      throw new Error('Expected the nextReducer to be a function');
    }
    
    this.appState.reducers.root = nextReducer;
    console.log('🔄 Reducer replaced');
  }
  
  /**
   * Hot reload support for development
   * @param {Object} newModules - New module exports
   */
  hotReload(newModules) {
    if (!import.meta.env?.DEV) {
      console.warn('Hot reload is only available in development');
      return;
    }
    
    let reloaded = [];
    
    if (newModules.reducers) {
      this.replaceReducer(newModules.reducers.rootReducer);
      reloaded.push('reducers');
    }
    
    if (newModules.middleware) {
      // Middleware hot reload would require recreating the pipeline
      console.log('⚠️ Middleware hot reload requires app restart');
    }
    
    console.log(`🔥 Hot reloaded: ${reloaded.join(', ')}`);
  }
}

/**
 * Create the enhanced store instance
 */
const store = new Store();

/**
 * Convenience API Functions
 */

// State access
export const getState = store.getState;
export const select = store.select.bind(store);

// Actions
export const dispatch = store.dispatch;
export const batchDispatch = store.batchDispatch.bind(store);

// Subscriptions
export const subscribe = store.subscribe;

// Utilities
export const getStats = store.getStats.bind(store);
export const reset = store.reset.bind(store);
export const snapshot = store.snapshot.bind(store);

/**
 * Domain-specific dispatchers for convenience
 */
export const ui = {
  setCurrentTab: (tab) => dispatch(actions.setCurrentTab(tab)),
  setLoading: (loading, message) => dispatch(actions.setLoading(loading, message)),
  showModal: (modal, props) => dispatch(actions.showModal(modal, props)),
  hideModal: () => dispatch(actions.hideModal()),
  toggleSidebar: () => dispatch(actions.toggleSidebar()),
  setTheme: (theme) => dispatch(actions.setTheme(theme)),
  showToast: (message, type, duration) => dispatch(actions.showToast(message, type, duration))
};

export const initiatives = {
  add: (initiative) => dispatch(actions.addInitiative(initiative)),
  update: (id, updates) => dispatch(actions.updateInitiative(id, updates)),
  delete: (id) => dispatch(actions.deleteInitiative(id)),
  select: (id) => dispatch(actions.selectInitiative(id)),
  setAll: (initiatives) => dispatch(actions.setInitiatives(initiatives))
};

export const chat = {
  addMessage: (content, sender, transfer) => dispatch(actions.addChatMessage(content, sender, transfer)),
  clear: () => dispatch(actions.clearChat()),
  setTyping: (typing) => dispatch(actions.setTyping(typing)),
  updateContext: (context) => dispatch(actions.updateConversationContext(context))
};

export const user = {
  setApiKey: (key) => dispatch(actions.setApiKey(key)),
  setModel: (model) => dispatch(actions.setModel(model)),
  updateSettings: (settings) => dispatch(actions.updateSettings(settings)),
  updatePreferences: (prefs) => dispatch(actions.updatePreferences(prefs))
};

export const strategies = {
  setUploaded: (strategy) => dispatch(actions.setUploadedStrategy(strategy)),
  setEmergent: (strategy) => dispatch(actions.setEmergentStrategy(strategy)),
  setCurrent: (strategy) => dispatch(actions.setCurrentStrategy(strategy)),
  clear: () => dispatch(actions.clearStrategies())
};

export const portfolio = {
  setAnalysis: (analysis) => dispatch(actions.setPortfolioAnalysis(analysis)),
  setConflicts: (conflicts) => dispatch(actions.setPortfolioConflicts(conflicts)),
  updateStatus: (status) => dispatch(actions.updatePortfolioStatus(status))
};

/**
 * Async action dispatchers
 */
export const async = {
  saveInitiative: (initiative) => dispatch(asyncActions.saveInitiativeAsync(initiative)),
  loadInitiatives: () => dispatch(asyncActions.loadInitiativesAsync()),
  deleteInitiative: (id) => dispatch(asyncActions.deleteInitiativeAsync(id)),
  sendChatMessage: (message) => dispatch(asyncActions.sendChatMessageAsync(message))
};

// Export main store instance and components
export default store;
export { store };

// Export all store components for advanced usage
export {
  actions,
  ActionTypes,
  asyncActions,
  rootReducer,
  selectors,
  createDefaultMiddleware,
  composeMiddleware,
  applyMiddleware
};

// Export individual reducers for testing/debugging
export const reducers = {
  root: rootReducer,
  ui: uiReducer,
  initiatives: initiativesReducer,
  chat: chatReducer,
  user: userReducer,
  strategies: strategiesReducer,
  portfolio: portfolioReducer,
  system: systemReducer,
  selection: selectionReducer
};

// Export middleware components
export const middleware = {
  logger: loggerMiddleware,
  persistence: persistenceMiddleware,
  analytics: analyticsMiddleware,
  async: asyncMiddleware,
  validation: validationMiddleware,
  performance: performanceMiddleware,
  eventBridge: eventBridgeMiddleware,
  error: errorMiddleware,
  debounce: debounceMiddleware,
  devTools: devToolsMiddleware
};

// Attach to window for debugging in development
if (import.meta.env?.DEV) {
  window.Store = store;
  window.StoreAPI = {
    // State
    getState,
    select,
    
    // Actions
    dispatch,
    batchDispatch,
    actions,
    ActionTypes,
    asyncActions,
    
    // Domain APIs
    ui,
    initiatives,
    chat,
    user,
    strategies,
    portfolio,
    async,
    
    // Components
    reducers,
    middleware,
    selectors,
    
    // Utilities
    getStats,
    reset,
    snapshot,
    
    // Store instance
    store
  };
  
  console.log('🔧 Enhanced Store API available at window.StoreAPI');
  console.log('🔧 Store instance available at window.Store');
  console.log('📊 Store Stats:', store.getStats());
}

// Log successful initialization
console.log('🎉 Redux-Enhanced Store System Ready!');
console.log('📈 Features:', store.meta.features.join(', '));
